import { useEffect, useState } from 'react';
import { ApiError } from './api';

/**
 * A button that has to wait. The API refuses a repeat send inside a minute
 * with `too_soon` and says how long is left; the button counts that down
 * and comes back on its own, so the person sees when they may try again
 * rather than an error that reads like a failure.
 */

/** Seconds the API asked us to wait, from a `too_soon` refusal; 0 for anything else. */
export function retryAfterSec(error: unknown, fallback = 60): number {
  if (!(error instanceof ApiError) || error.code !== 'too_soon') return 0;
  const details = error.details as { retryAfterSec?: unknown } | undefined;
  const value = typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : fallback;
  return Math.max(1, Math.ceil(value));
}

export function useCooldown(): { secondsLeft: number; start: (seconds: number) => void } {
  const [until, setUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until === null) return;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (current >= until) setUntil(null);
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [until]);

  const secondsLeft = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
  return { secondsLeft, start: (seconds) => setUntil(Date.now() + seconds * 1000) };
}
